import { EditorView } from '@codemirror/view';
import { Extension } from '@codemirror/state';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

// PropAlgos color palette - matching demo section
const colors = {
  // Background colors - glassy effect
  bg: 'rgba(0, 0, 0, 0.5)',        // Semi-transparent black
  bgLight: 'rgba(31, 41, 55, 0.5)', // Semi-transparent dark gray
  bgHighlight: 'rgba(55, 65, 81, 0.5)', // Semi-transparent highlight
  
  // Text colors
  text: '#ffffff',         // White text for better contrast
  textMuted: '#9ca3af',    // Muted gray
  
  // Syntax colors - matching demo exactly
  keyword: '#a78bfa',      // Purple for import/from/def/return
  string: '#10b981',       // Green for strings
  function: '#3b82f6',     // Blue for function names
  variable: '#ffffff',     // White for variables
  number: '#fbbf24',       // Bright yellow for numbers
  comment: '#6b7280',      // Gray for comments
  operator: '#ffffff',     // White for operators
  className: '#60a5fa',    // Light blue for classes
  constant: '#ef4444',     // Red for constants
  
  // GPU-specific
  gpuAccent: '#f97316',    // Orange accent
  
  // UI colors
  border: 'rgba(55, 65, 81, 0.5)', // Semi-transparent border
  focus: '#3b82f6',
  error: '#ef4444',
  warning: '#f59e0b',
  success: '#10b981',
};

// Editor theme with sharp corners and glassy effect
export const propalgosTheme = EditorView.theme({
  '&': {
    color: colors.text,
    backgroundColor: colors.bg,
    fontSize: '13px',
    fontFamily: "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
    // No backdrop-filter here — parent cell wrapper provides the glass effect.
    // backdrop-filter creates a CSS containing block that traps position:fixed tooltips.
  },
  
  '.cm-content': {
    padding: '16px 20px',
    minHeight: '60px',
    caretColor: colors.gpuAccent,
    textAlign: 'left',
  },
  
  '.cm-focused': {
    outline: 'none',
  },
  
  '.cm-editor': {
    borderRadius: '0', // Sharp corners
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  
  '.cm-editor.cm-focused': {
    borderColor: colors.focus,
    boxShadow: `0 0 0 2px ${colors.focus}33, 0 4px 6px -1px rgba(0, 0, 0, 0.1)`,
  },
  
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: colors.gpuAccent,
    borderLeftWidth: '2px',
  },
  
  '.cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: `${colors.bgHighlight}99`,
  },
  
  '.cm-activeLine': {
    backgroundColor: `${colors.bgLight}66`,
  },
  
  '.cm-activeLineGutter': {
    backgroundColor: colors.bgLight,
  },
  
  '.cm-gutters': {
    backgroundColor: colors.bgLight,
    color: colors.textMuted,
    borderRight: `1px solid ${colors.border}`,
    borderTopLeftRadius: '0',
    borderBottomLeftRadius: '0',
  },
  
  '.cm-lineNumbers .cm-gutterElement': {
    padding: '0 8px',
    minWidth: '40px',
  },
  
  '.cm-foldGutter': {
    color: colors.textMuted,
  },
  
  '.cm-foldGutter .cm-gutterElement': {
    padding: '0 4px',
  },
  
  '.cm-foldPlaceholder': {
    backgroundColor: colors.bgHighlight,
    border: 'none',
    color: colors.textMuted,
    borderRadius: '0', // Sharp corners
    padding: '0 4px',
  },
  
  '.cm-searchMatch': {
    backgroundColor: `${colors.warning}33`,
    outline: `1px solid ${colors.warning}66`,
  },
  
  '.cm-searchMatch.cm-searchMatch-selected': {
    backgroundColor: `${colors.warning}66`,
  },
  
  '.cm-panels': {
    backgroundColor: colors.bgLight,
    color: colors.text,
    borderTop: `1px solid ${colors.border}`,
  },
  
  '.cm-panels.cm-panels-top': {
    borderBottom: `1px solid ${colors.border}`,
    borderTop: 'none',
  },
  
  '.cm-panel input': {
    backgroundColor: colors.bg,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    padding: '2px 6px',
  },
  
  '.cm-panel button': {
    backgroundColor: colors.focus,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '2px 8px',
    cursor: 'pointer',
  },
  
  '.cm-tooltip': {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '0',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
    zIndex: '9999 !important',
  },
  
  '.cm-tooltip-autocomplete': {
    '& > ul': {
      fontFamily: 'JetBrains Mono, Monaco, Consolas, monospace',
      fontSize: '13px',
    },
    '& > ul > li[aria-selected]': {
      backgroundColor: colors.bgHighlight,
      color: colors.text,
    },
  },
  
  '.cm-completionIcon': {
    paddingRight: '4px',
  },
  
  '.cm-completionIcon-function::after': { content: '"ƒ"' },
  '.cm-completionIcon-method::after': { content: '"m"' },
  '.cm-completionIcon-class::after': { content: '"○"' },
  '.cm-completionIcon-interface::after': { content: '"◌"' },
  '.cm-completionIcon-variable::after': { content: '"𝑥"' },
  '.cm-completionIcon-constant::after': { content: '"C"' },
  '.cm-completionIcon-type::after': { content: '"T"' },
  '.cm-completionIcon-enum::after': { content: '"E"' },
  '.cm-completionIcon-property::after': { content: '"•"' },
  '.cm-completionIcon-keyword::after': { content: '"K"' },
  '.cm-completionIcon-namespace::after': { content: '"N"' },
  '.cm-completionIcon-module::after': { content: '"M"' },
  
  '.cm-completionDetail': {
    marginLeft: '8px',
    color: colors.textMuted,
    fontSize: '12px',
  },
  
  '.cm-lintRange': {
    backgroundImage: 'none',
    textDecoration: 'underline',
    textDecorationStyle: 'wavy',
  },
  
  '.cm-lintRange-error': {
    textDecorationColor: colors.error,
  },
  
  '.cm-lintRange-warning': {
    textDecorationColor: colors.warning,
  },
  
  '.cm-lintRange-info': {
    textDecorationColor: colors.focus,
  },
  
  '.cm-diagnostic': {
    backgroundColor: colors.bgLight,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    padding: '4px 8px',
    marginTop: '4px',
  },
  
  '.cm-diagnostic-error': {
    borderLeftColor: colors.error,
    borderLeftWidth: '3px',
  },
  
  '.cm-diagnostic-warning': {
    borderLeftColor: colors.warning,
    borderLeftWidth: '3px',
  },
});

// Syntax highlighting
export const propalgosHighlightStyle = HighlightStyle.define([
  { tag: t.keyword, color: colors.keyword, fontWeight: 'bold' },
  { tag: t.operator, color: colors.operator },
  { tag: t.special(t.variableName), color: colors.gpuAccent, fontWeight: 'bold' }, // GPU-related
  { tag: t.variableName, color: colors.variable },
  { tag: t.function(t.variableName), color: colors.function },
  { tag: t.definition(t.variableName), color: colors.variable, fontWeight: 'bold' },
  { tag: t.string, color: colors.string },
  { tag: t.number, color: colors.number },
  { tag: t.bool, color: colors.constant, fontWeight: 'bold' },
  { tag: t.null, color: colors.constant, fontWeight: 'bold' },
  { tag: t.comment, color: colors.comment, fontStyle: 'italic' },
  { tag: t.className, color: colors.className, fontWeight: 'bold' },
  { tag: t.definition(t.className), color: colors.className, fontWeight: 'bold' },
  { tag: t.typeName, color: colors.className },
  { tag: t.propertyName, color: colors.variable },
  { tag: t.attributeName, color: colors.function },
  { tag: t.regexp, color: colors.string },
  { tag: t.escape, color: colors.warning },
  { tag: t.meta, color: colors.gpuAccent }, // Used for decorators in Python
  { tag: t.monospace, fontFamily: 'JetBrains Mono, Monaco, Consolas, monospace' },
  
  // Brackets and punctuation
  { tag: t.bracket, color: colors.textMuted },
  { tag: t.punctuation, color: colors.textMuted },
  { tag: t.angleBracket, color: colors.textMuted },
  { tag: t.squareBracket, color: colors.textMuted },
  { tag: t.paren, color: colors.textMuted },
  { tag: t.brace, color: colors.textMuted },
]);

// Complete theme extension
export const propalgos: Extension = [
  propalgosTheme,
  syntaxHighlighting(propalgosHighlightStyle),
];